<!-- swipe-back edge indicator -->
<div class="swipe-back-indicator" [class.active]="swipeIndicatorActive()"></div>

<!-- swarm toggle in command-line header (mobile only, when input is open) -->
@if (isMobile() && inputVisible()) {
  <button class="mobile-mesh-header-btn" [class.shield-mode]="meshPublic()"
          (click)="toggleMeshPublic()"
          [attr.aria-label]="(meshPublic() ? 'mesh.swarm' : 'mesh.solo') | t">
    <span class="hci">{{ meshPublic() ? 'e' : 'i' }}</span>
  </button>
}

<!-- breadcrumb: below header, right-aligned -->
<div class="breadcrumb-top"
     [class.faded]="isMobile() && inputVisible()"
     [class.tappable]="isMobile()"
     (click)="isMobile() ? openSlashCommand() : null">
  @if (meshPublic()) {
    <span class="public-crumb" [style.color]="shieldColor()">{{ 'breadcrumb.public' | t }}</span><span class="sep-crumb">◆</span>
  } @else {
    <span class="private-crumb">{{ 'breadcrumb.private' | t }}</span><span class="sep-crumb">◆</span>
  }
  <span class="domain-crumb crumb-link" (click)="navigateTo([])">{{ activeDomain() }}</span>
  @for (seg of pathSegments(); track seg.target.length; let last = $last) {
    @if (last) {
      <span class="sep-crumb">◆</span><span class="branch-crumb">{{ seg.name }}</span>
    } @else {
      <span class="domain-crumb crumb-link" (click)="navigateTo(seg.target)"><span class="sep-crumb">◆</span>{{ seg.name }}</span>
    }
  }
  @if (secretWords()) {
    <span class="sep-crumb">◆</span><span class="secret-crumb">{{ secretWords() }}</span>
  }
  @if (hasSecret()) {
    <span class="sep-crumb">◆</span><span class="secure-crumb" [style.color]="shieldColor()">{{ 'breadcrumb.secure' | t }}</span>
  } @else {
    <span class="sep-crumb">◆</span><span class="unsecure-crumb">{{ 'breadcrumb.unsecured' | t }}</span>
  }
  @if (installState() !== 'idle') {
    <span class="sep-crumb">◆</span>
    <span class="install-crumb" [attr.data-state]="installState()">
      @switch (installState()) {
        @case ('checking') { {{ 'breadcrumb.checking' | t }} }
        @case ('syncing') { {{ 'breadcrumb.installing' | t }} }
        @case ('complete') { {{ installChangedFiles() ? ('breadcrumb.installed-count' | t: { count: installChangedFiles() }) : ('breadcrumb.installed' | t) }} }
        @case ('error') { {{ 'breadcrumb.sync-error' | t }} }
      }
    </span>
  }
</div>

@if (tags().length > 0 || globalOnlyTags().length > 0) {
  <div class="tag-float" [class.tag-expanded]="tagPanelOpen()">
    <span class="tag-lead-icon hci" (click)="toggleTagPanel()">X</span>
    @for (tag of tags(); track tag.name) {
      <button class="tag-crumb"
              [class.tag-active]="isTagFilterActive(tag.name)"
              [class.tag-hovered]="isTagHovered(tag.name)"
              [style.color]="tagColor(tag.name)"
              [style.borderColor]="tagColor(tag.name)"
              (click)="toggleTagFilter(tag.name)">{{ tag.name }}</button>
    }
    @if (globalOnlyTags().length > 0 && tags().length > 0) {
      <span class="tag-separator"></span>
    }
    @for (name of globalOnlyTags(); track name) {
      <button class="tag-crumb tag-global"
              (click)="toggleTagFilter(name)">{{ name }}</button>
    }
  </div>
}

@if (isMobile() && !inputVisible()) {
  <div class="mobile-onboarding-hint">long-press to type · tap breadcrumb for /</div>
}

<div
  class="pill-stage"
  [class.faded]="!visible()"
  [class.positioned]="!!pillPos() && !isMobile()"
  [class.dragging]="pillDragging()"
  [class.landscape]="isLandscape()"
  [class.mobile]="isMobile()"
  [style.left.px]="!isMobile() ? pillPos()?.x : null"
  [style.top.px]="!isMobile() ? pillPos()?.y : null"
  (mouseenter)="onBarEnter()"
  (mouseleave)="onBarLeave()"
>
  <!-- zoomed wrapper keeps pill visuals 2× without scaling stage position -->
  <div class="pill-scaled" [style.zoom]="pillZoom()">
  <div
    class="controls-pill"
    [class.public-mode]="meshPublic()"
    [class.power-ctrl]="powerKey() === 'ctrl'"
    [class.power-shift]="powerKey() === 'shift'"
    [class.power-alt]="powerKey() === 'alt'"
  >
  <!-- drag handle (move only; no resize) — first child inside the pill -->
  @if (!isMobile()) {
    <div class="drag-handle vertical"
         (pointerdown)="onPillDragStart($event)"
         [attr.aria-label]="'controls.drag-to-move' | t">
      <span class="dot"></span><span class="dot"></span>
      <span class="dot"></span><span class="dot"></span>
      <span class="dot"></span><span class="dot"></span>
      <span class="dot"></span><span class="dot"></span>
    </div>
  }
  @switch (mode()) {
    @case ('browsing') {

      @if (isMobile()) {
        <!-- ═══ MOBILE: floating icons (no chrome) — Material Symbols ═══ -->
        <!-- back · talk · camera · center · fullscreen
             (solo/swarm toggle lives in the command-line header) -->
        <div class="controls-section mobile-nav">
          <button class="ctl-btn" [class.disabled]="!canGoBack()" [disabled]="!canGoBack()"
                  (pointerdown)="onBackPointerDown($event)" (click)="onBackClick()"
                  [attr.aria-label]="'controls.go-back' | t">
            <span class="mat-sym">arrow_back</span>
          </button>
          @if (voiceSupported) {
            <button class="ctl-btn mic-btn" [class.recording]="voiceActive()"
              (pointerdown)="mobileMicDown($event)" (pointerup)="mobileMicUp()" (pointercancel)="mobileMicUp()" (pointerleave)="mobileMicUp()"
              [attr.aria-label]="'controls.voice-hold' | t">
              <span class="mat-sym" [class.filled]="voiceActive()">mic</span>
            </button>
          }
          <button class="ctl-btn camera-btn" (click)="openCameraCapture()" [attr.aria-label]="'controls.camera' | t"></button>
          <button class="ctl-btn fit-btn"
                  [class.fit-global]="fitButtonState() === 'global'"
                  [class.fit-page]="fitButtonState() === 'page'"
                  (click)="fitOrCenter($event)" (dblclick)="lockFit()"
                  [attr.aria-label]="'controls.fit-content' | t">
            <span class="mat-sym">center_focus_strong</span>
          </button>
          <button class="ctl-btn" (click)="toggleFullscreen()" [attr.aria-label]="'controls.fullscreen' | t">
            <span class="mat-sym">fullscreen</span>
          </button>
        </div>
      } @else {
        <!-- ═══ DESKTOP: single-row controls with edit-mode toggle ═══ -->
        <!-- One flat row of every visible control. Items the user has muted
             render grayed and click-no-op in normal mode. The chevron at the
             end of the row toggles edit mode — while editing, every click
             flips an item's enabled state instead of running its action. -->
        <div class="controls-rows" [class.editing]="editMode()">
          <div class="controls-row">
            @for (ctrl of visibleControls(); track ctrl.id) {
              <button class="ctl-btn"
                      [class.active]="ctrl.id !== 'fit' && isActive(ctrl)"
                      [class.muted]="!isEnabled(ctrl)"
                      [class.editing]="editMode()"
                      [class.eye-btn]="ctrl.id === 'show-hidden'"
                      [class.text-only-btn]="ctrl.id === 'text-only'"
                      [class.mic-btn]="ctrl.id === 'voice'"
                      [class.bee-btn]="ctrl.id === 'bees'"
                      [class.clipboard-btn]="ctrl.id === 'clipboard'"
                      [class.fit-btn]="ctrl.id === 'fit'"
                      [class.fit-global]="ctrl.id === 'fit' && fitButtonState() === 'global'"
                      [class.fit-page]="ctrl.id === 'fit' && fitButtonState() === 'page'"
                      [class.zoom-btn]="ctrl.id === 'zoom-in' || ctrl.id === 'zoom-out'"
                      (click)="onCtrlClick(ctrl, $event)"
                      [attr.aria-label]="ctrl.label | t"
                      [attr.data-instruction]="ctrl.instruction ?? null">
                <!-- Material Symbols Outlined — every desktop control
                     maps to a Symbol name via iconSymbol(). The .filled
                     modifier flips the FILL axis on stateful toggles
                     (voice/bees/etc.) so active controls read distinctly
                     without needing a separate icon glyph. -->
                <span class="mat-sym"
                      [class.filled]="isActive(ctrl) && ctrl.id !== 'fit'">
                  {{ iconSymbol(ctrl) }}
                </span>
                @if (ctrl.id === 'clipboard' && badgeValue(ctrl) > 0) {
                  <span class="badge">{{ badgeValue(ctrl) }}</span>
                }
              </button>
            }
          </div>
        </div>
        <!-- edit-mode toggle (chevron) -->
        <button class="ctl-btn expand-toggle"
                [class.editing]="editMode()"
                (click)="toggleEditMode()"
                [attr.aria-label]="editMode() ? ('controls.collapse' | t) : ('controls.expand' | t)">
          <span class="expand-chevron" [class.up]="editMode()">&#x25B8;</span>
        </button>
      }

    }

    @case ('clipboard') {
      @if (clipboardAvailable()) {
        <div class="controls-section clipboard-mode">
          <span class="mode-label">{{ 'controls.clipboard' | t }}</span>
          @if (clipboardCount() > 0) {
            <span class="clipboard-count">{{ clipboardCount() }}</span>
          } @else {
            <span class="clipboard-empty">{{ 'controls.clipboard-empty' | t }}</span>
          }
        </div>
        <div class="controls-divider"></div>
        @if (clipboardCount() > 0) {
          <button class="ctl-btn" (click)="place()" [attr.aria-label]="'controls.place-selected' | t">
            <span class="mat-sym" [style.color]="hasSelection() ? '#4caf50' : ''">place_item</span>
          </button>
          <button class="ctl-btn" (click)="paste()" [attr.aria-label]="'controls.paste-clipboard' | t">
            <span class="mat-sym" style="color:#4caf50">content_paste</span>
          </button>
        }
        <button class="ctl-btn" (click)="clearClipboard()" [attr.aria-label]="'controls.clear-clipboard' | t">
          <span class="mat-sym">delete</span>
        </button>
        <button class="ctl-btn" (click)="closeClipboard()" [attr.aria-label]="'controls.close-clipboard' | t">
          <span class="mat-sym">close</span>
        </button>
      }
    }

    @case ('atomize') {
      <div class="controls-section atomize-mode">
        <span class="mode-label">{{ 'controls.atomize' | t }}</span>
        <span class="atomize-count">{{ atomizeAtomCount() }}</span>
      </div>
      <div class="controls-divider"></div>
      <!-- strategy picker: 5 icons -->
      <div class="controls-section strategy-picker">
        @for (name of STRATEGY_NAMES; track name) {
          <button class="ctl-btn strategy-btn"
                  [class.active]="atomizeStrategy() === name"
                  (click)="setAtomizeStrategy(name)"
                  [attr.aria-label]="'controls.atomize-' + name | t">
            <span class="strategy-icon" [attr.data-strategy]="name">{{ name.charAt(0).toUpperCase() }}</span>
          </button>
        }
      </div>
      <div class="controls-divider"></div>
      <button class="ctl-btn" (click)="reassemble()" [attr.aria-label]="'controls.atomize-reassemble' | t">
        <span class="mat-sym" style="color:#4caf50">join_full</span>
      </button>
      <button class="ctl-btn" (click)="closeAtomize()" [attr.aria-label]="'controls.atomize-close' | t">
        <span class="mat-sym">close</span>
      </button>
    }

  }
  </div>
  </div>
</div>
